import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

const formatBytes = (bytes) => {
  const n = Number(bytes || 0);
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
};

export default function EmailCampaignAttachmentsStep({
  attachments = [],
  limits,
  onUpload,
  onDelete,
  uploadLoading = false,
  readOnly = false,
}) {
  const [error, setError] = useState('');

  const totalSize = useMemo(
    () => attachments.reduce((sum, item) => sum + Number(item?.size_bytes || 0), 0),
    [attachments]
  );

  const handleSelect = async (event) => {
    const files = Array.from(event.target.files || []);
    setError('');

    if (!files.length || readOnly) {
      return;
    }

    const maxFiles = Number(limits?.max_files || 10);
    const maxTotal = Number(limits?.max_total_size_bytes || 20 * 1024 * 1024);

    if ((attachments.length + files.length) > maxFiles) {
      setError(`Maximum ${maxFiles} files are allowed.`);
      return;
    }

    const selectedTotal = files.reduce((sum, file) => sum + Number(file.size || 0), 0);
    if ((totalSize + selectedTotal) > maxTotal) {
      setError('Total size exceeds allowed limit.');
      return;
    }

    await onUpload?.(files);
    event.target.value = '';
  };

  return (
    <Stack spacing={2}>
      {error ? <Alert severity="error">{error}</Alert> : null}

      <Box
        sx={{
          p: 2,
          border: '1px dashed',
          borderColor: 'divider',
          borderRadius: 1.5,
        }}
      >
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'center' }}>
          <Button variant="outlined" component="label" disabled={readOnly || uploadLoading}>
            {uploadLoading ? 'Uploading...' : 'Upload files'}
            <input hidden type="file" multiple onChange={handleSelect} />
          </Button>

          <Typography variant="body2" color="text.secondary">
            Allowed: {(limits?.allowed_extensions || []).join(', ') || 'configured by backend'}
          </Typography>
        </Stack>
      </Box>

      <Typography variant="body2" color="text.secondary">
        {attachments.length} attachment(s), total size: {formatBytes(totalSize)}
      </Typography>

      <List dense disablePadding>
        {attachments.map((attachment) => (
          <ListItem key={attachment.id} divider>
            <ListItemText
              primary={attachment.original_name}
              secondary={`${attachment.mime || '-'} � ${formatBytes(attachment.size_bytes)}`}
            />

            {!readOnly ? (
              <ListItemSecondaryAction>
                <IconButton edge="end" color="error" onClick={() => onDelete?.(attachment.id)}>
                  <DeleteOutlineIcon />
                </IconButton>
              </ListItemSecondaryAction>
            ) : null}
          </ListItem>
        ))}
      </List>
    </Stack>
  );
}
